import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    ADMIN_HOME_PATH,
    buildAdminBreadcrumbs,
    getAdminCompanyBreadcrumbLabel,
    getParentAdminBreadcrumb,
    isAdminDetailRoute,
    readAdminOrgId,
    type AdminFromCompanyDetailState,
} from '../lib/adminBreadcrumbs';
import { useAccessApi } from '../services/api';
import { queryKeys } from './useApi';

function parseNumericId(value: string | undefined): number | null {
    return value && /^\d+$/.test(value) ? Number(value) : null;
}

export function useAdminBreadcrumbBack(fallbackPath: string = ADMIN_HOME_PATH) {
    const location = useLocation();
    const navigate = useNavigate();
    const { getOrganizationById, getOrganizations } = useAccessApi();

    const pathnames = location.pathname.split('/').filter((x) => x);
    const adminIndex = pathnames.indexOf('admin');
    const orgId = readAdminOrgId(pathnames, adminIndex);

    const navState = location.state as AdminFromCompanyDetailState | null | undefined;
    const fromCompanyDetail = navState?.fromCompanyDetail;
    const fromSowDetail = navState?.fromSowDetail;
    const adminCompanyDetail = navState?.adminCompanyDetail;
    const isAdminSowDetail = isAdminDetailRoute(pathnames, adminIndex, 'sows');
    const isAdminPricingConfigDetail = isAdminDetailRoute(pathnames, adminIndex, 'pricing-configs');

    const adminCompaniesDetailNumericId =
        adminIndex >= 0 && pathnames[adminIndex + 1] === 'companies'
            ? parseNumericId(pathnames[adminIndex + 2])
            : null;

    const fromCoCompanyNumericId = parseNumericId(fromCompanyDetail?.companyId);
    const fromSowCompanyNumericId = parseNumericId(fromSowDetail?.companyId);

    const organizationsLookupNumericId =
        adminCompaniesDetailNumericId ??
        (isAdminSowDetail ? fromCoCompanyNumericId : null) ??
        (isAdminPricingConfigDetail && fromSowCompanyNumericId !== null
            ? fromSowCompanyNumericId
            : null);

    const { data: organization } = useQuery({
        queryKey: ['organization', orgId],
        queryFn: () => {
            if (!orgId) return Promise.resolve(null);
            return getOrganizationById(orgId).catch(() => null);
        },
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: creditPortalOrganizations } = useQuery({
        queryKey: [...queryKeys.all, 'organizations'],
        queryFn: getOrganizations,
        enabled:
            organizationsLookupNumericId != null &&
            Number.isFinite(organizationsLookupNumericId) &&
            organizationsLookupNumericId > 0,
        staleTime: 5 * 60 * 1000,
    });

    const parentBreadcrumb = useMemo(() => {
        const breadcrumbs = buildAdminBreadcrumbs({
            pathnames,
            organizationLabel: organization?.display_name || 'Organization',
            adminCompanyDetail,
            fromCompanyDetail,
            fromSowDetail,
            getAdminCompanyLabel: (numericCompanyId) =>
                getAdminCompanyBreadcrumbLabel(creditPortalOrganizations, numericCompanyId),
        });
        return getParentAdminBreadcrumb(breadcrumbs);
    }, [
        adminCompanyDetail,
        creditPortalOrganizations,
        fromCompanyDetail,
        fromSowDetail,
        organization?.display_name,
        pathnames,
    ]);

    const goBack = useCallback(() => {
        if (parentBreadcrumb) {
            navigate(parentBreadcrumb.path, {
                ...(parentBreadcrumb.state !== undefined ? { state: parentBreadcrumb.state } : {}),
            });
            return;
        }
        navigate(fallbackPath);
    }, [fallbackPath, navigate, parentBreadcrumb]);

    return { goBack, parentBreadcrumb };
}
